import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import handleLogin, { clearRefreshTimer } from "../services/auth.js"

interface User {
  id: string
  username: string
  role: string
  canAccessPolicySearch: boolean
  canManagePolicyDocuments: boolean
  totalReports: number
  reportsThisMonth: number
  remainingReports: number
  access_token: string | null
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Fetch the user's profile (role + policy-search permission) from the backend.
// Uses raw fetch (not the axios instance) to avoid the 401 interceptor
// redirecting to /login during the login flow. Falls back to the JWT-derived
// role if the profile call fails.
async function fetchProfile(
  token: string,
  fallbackRole: string
): Promise<{ role: string; canAccessPolicySearch: boolean; canManagePolicyDocuments: boolean } | null> {
  try {
    const baseURL = (import.meta as any).env?.VITE_API_BASE_URL || ""
    const res = await fetch(`${baseURL}/api/v1/users/profile/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return null
    const json = await res.json()
    // The ApiRenderer wraps responses in { success, data, ... } — unwrap if needed.
    const data = json?.data ?? json
    return {
      role: data?.role || fallbackRole,
      canAccessPolicySearch: !!data?.can_access_policy_search,
      canManagePolicyDocuments: !!data?.can_manage_policy_documents,
    }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const res = await handleLogin({ username, password })

      if ("access_token" in res && res.access_token && res.user_id) {
        const fallbackRole = res.is_admin ? "Admin" : "User"
        const profile = await fetchProfile(res.access_token, fallbackRole)
        const role = profile?.role || fallbackRole
        const canAccessPolicySearch = profile?.canAccessPolicySearch ?? false
        const canManagePolicyDocuments = profile?.canManagePolicyDocuments ?? false

        const userData: User = {
          id: res.user_id.toString(),
          username,
          role,
          canAccessPolicySearch,
          canManagePolicyDocuments,
          totalReports: 0,
          reportsThisMonth: 0,
          remainingReports: 0,
          access_token: res.access_token,
        }

        setUser(userData)
        localStorage.setItem("crediflow_user", JSON.stringify(userData))
        return true
      }

      return false
    } catch (error) {
      console.error("Login error:", error)
      return false
    }
  }

  const logout = () => {
    clearRefreshTimer()
    setUser(null)
    localStorage.removeItem("crediflow_user")
    localStorage.removeItem("is_admin")
    localStorage.removeItem("user_id")
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
  }

  useEffect(() => {
    const storedUser = localStorage.getItem("crediflow_user")
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        if (!parsedUser.role) {
          parsedUser.role = "User" // default if missing
        }
        if (parsedUser.canAccessPolicySearch === undefined) {
          parsedUser.canAccessPolicySearch = false
        }
        if (parsedUser.canManagePolicyDocuments === undefined) {
          parsedUser.canManagePolicyDocuments = false
        }
        setUser(parsedUser)
      } catch (error) {
        console.error("Error parsing stored user:", error)
        localStorage.removeItem("crediflow_user")
      }
    }
    setIsLoading(false) // mark hydration complete
  }, [])

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
